import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useLocalStorageValue } from '@react-hookz/web';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { InvoiceMetadata } from '@/components/accounts-payable/invoice-detail/invoice-metadata';
import { InvoiceLineItems } from '@/components/accounts-payable/invoice-detail/invoice-line-items';
import { ThreeWayMatchDialog } from '@/components/accounts-payable/invoice-detail/ThreeWayMatchDialog';
import { ChargesTable } from '@/components/common/charges-table';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { useChargesForEntity } from '@/api/rpc/hooks/finance/useCharges';
import { useEntityAssociationsList } from '@/api/rpc/hooks/core/useEntityAssociations';
import { orpc } from '@/api/rpc/client';
import type { APInvoice, InboundEmail } from '@erp/rpc';
import {
  APInvoiceStatus,
  SystemEntity,
  EntityAssociationType,
} from '@erp/shared-types';
import { SyncToERPButton } from '@/components/accounts-payable/invoice-detail/integrations';
import { useApproveAPInvoice } from '@/api/rpc/hooks/accounts-payable/useApproveAPInvoice';
import { useDeleteAPInvoice } from '@/api/rpc/hooks/accounts-payable/useDeleteAPInvoice';
import { useNavigate } from '@tanstack/react-router';
import { Can } from '@/context/authz';
import { Action, Subject } from '@erp/authz';
import { TableIcon, CheckCircleIcon } from '@phosphor-icons/react';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { OriginSyncStatus } from '@erp/shared-types';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { CalendarIcon, InfoIcon } from '@phosphor-icons/react';

export interface DerivedAssociation {
  entityType: SystemEntity;
  entityId: string;
}

/**
 * Hook to derive parent document associations from charge matches on line items.
 * When a line item is matched to a charge from a SO or Shipment, we derive
 * that the invoice is related to that parent document.
 */
function useDerivedAssociations(
  invoiceId: string,
  lineItemIds: string[],
): DerivedAssociation[] {
  // Fetch all line item associations (this gets us the charge matches)
  const lineItemQueries = useQueries({
    queries: useMemo(
      () =>
        lineItemIds.map((lineItemId) =>
          orpc.core.entityAssociations.list.queryOptions({
            input: {
              entityId: lineItemId,
              entityType: SystemEntity.AP_INVOICE_LINE_ITEM,
              associationType: EntityAssociationType.AP_INVOICE_MATCHING,
            },
          }),
        ),
      [lineItemIds],
    ),
  });

  // Extract charge IDs from line item associations
  const chargeIds = useMemo(() => {
    const ids: string[] = [];
    lineItemQueries.forEach((query) => {
      if (query.data?.associations) {
        query.data.associations.forEach((assoc) => {
          if (assoc.entity.entityType === SystemEntity.CHARGE) {
            ids.push(assoc.entity.entityId);
          }
        });
      }
    });
    return ids;
  }, [lineItemQueries]);

  // Fetch charge details to get parent document info
  const chargeQueries = useQueries({
    queries: useMemo(
      () =>
        chargeIds.map((chargeId) =>
          orpc.finance.charges.getById.queryOptions({
            input: { id: chargeId },
          }),
        ),
      [chargeIds],
    ),
  });

  // Get explicit associations for the invoice to filter out duplicates
  const { data: explicitAssociationsData } = useEntityAssociationsList({
    entityId: invoiceId,
    entityType: SystemEntity.AP_INVOICE,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });

  // Build set of explicit association keys for deduplication
  const explicitAssociationKeys = useMemo(() => {
    const keys = new Set<string>();
    if (explicitAssociationsData?.associations) {
      explicitAssociationsData.associations.forEach((assoc) => {
        keys.add(`${assoc.entity.entityType}:${assoc.entity.entityId}`);
      });
    }
    return keys;
  }, [explicitAssociationsData]);

  // Derive parent documents from charge matches
  const derivedAssociations = useMemo(() => {
    const derivedDocs = new Map<string, DerivedAssociation>();

    chargeQueries.forEach((query) => {
      const charge = query.data?.charge;
      if (charge?.docType && charge?.docId) {
        const entityType = charge.docType as SystemEntity;
        // Only include SO and Shipment associations (PO associations are handled explicitly)
        // Skip if this is already an explicit association
        const key = `${entityType}:${charge.docId}`;
        if (
          (entityType === SystemEntity.AR_SALES_ORDER ||
            entityType === SystemEntity.SCM_SHIPMENT) &&
          !explicitAssociationKeys.has(key)
        ) {
          derivedDocs.set(key, {
            entityType,
            entityId: charge.docId,
          });
        }
      }
    });

    return Array.from(derivedDocs.values());
  }, [chargeQueries, explicitAssociationKeys]);

  return derivedAssociations;
}

export interface InvoiceDetailsProps {
  invoice: APInvoice;
  inboundEmail?: InboundEmail | null;
  lineItems?: Array<{
    id: string;
    lineNumber: number;
    description?: string | null;
    quantity?: string | number | null;
    unitPrice?: string | null;
    totalPrice?: string | null;
  }>;
}

export function InvoiceDetails({
  invoice,
  inboundEmail,
  lineItems,
}: InvoiceDetailsProps) {
  const { value: panelSize, set: setPanelSize } = useLocalStorageValue<number>(
    'invoice-details-panel-size',
    {
      defaultValue: 66,
      initializeWithValue: true,
    },
  );

  const navigate = useNavigate();

  const invoiceAssociationsQuery = useEntityAssociationsList({
    entityId: invoice.id,
    entityType: SystemEntity.AP_INVOICE,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });

  const associatedPOIds = useMemo(() => {
    const associations = invoiceAssociationsQuery.data?.associations || [];
    const poIds = new Set<string>();
    associations.forEach((assoc) => {
      if (assoc.entity.entityType === SystemEntity.INTERNAL_PURCHASE_ORDER) {
        poIds.add(assoc.entity.entityId);
      }
    });
    return Array.from(poIds);
  }, [invoiceAssociationsQuery.data?.associations]);

  // Fetch charges for the invoice (only header charges)
  const { charges: allCharges } = useChargesForEntity({
    entityType: SystemEntity.AP_INVOICE,
    entityId: invoice.id,
    includeLineCharges: false,
  });

  // Filter to only header charges (no docLineId)
  const headerCharges = useMemo(
    () => allCharges.filter((charge) => !charge.docLineId),
    [allCharges],
  );

  // Derive parent document associations from charge matches on line items
  const lineItemIds = useMemo(
    () => lineItems?.map((li) => li.id) ?? [],
    [lineItems],
  );
  const derivedAssociations = useDerivedAssociations(invoice.id, lineItemIds);

  const lineItemAssociationQueries = useQueries({
    queries: useMemo(
      () =>
        lineItemIds.map((lineItemId) =>
          orpc.core.entityAssociations.list.queryOptions({
            input: {
              entityId: lineItemId,
              entityType: SystemEntity.AP_INVOICE_LINE_ITEM,
              associationType: EntityAssociationType.AP_INVOICE_MATCHING,
            },
          }),
        ),
      [lineItemIds],
    ),
  });

  const computedMatchSummary = useMemo(() => {
    if (invoice.lineItemMatchSummary) {
      return invoice.lineItemMatchSummary;
    }

    const total = lineItemIds.length;
    if (total === 0) return { total: 0, matched: 0 };

    let matched = 0;
    lineItemAssociationQueries.forEach((query) => {
      const associations = query.data?.associations || [];
      const hasMatch = associations.some(
        (assoc) =>
          assoc.entity.entityType ===
            SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM ||
          assoc.entity.entityType === SystemEntity.CHARGE,
      );
      if (hasMatch) matched++;
    });

    return { total, matched };
  }, [invoice.lineItemMatchSummary, lineItemIds, lineItemAssociationQueries]);

  const approveInvoice = useApproveAPInvoice();

  const deleteInvoice = useDeleteAPInvoice({
    onSuccess: (data) => {
      if (data.success) {
        navigate({ to: '..', viewTransition: true });
      }
    },
  });

  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [threeWayMatchOpen, setThreeWayMatchOpen] = useState(false);

  const isDraft = invoice.status === APInvoiceStatus.DRAFT;
  const isMatched = invoice.status === APInvoiceStatus.MATCHED;
  const isFinalized = invoice.status === APInvoiceStatus.FINALIZED;

  const hasLineItems = (lineItems?.length ?? 0) > 0;
  const hasSupplier = !!invoice.supplierId;
  const canApprove = (isDraft || isMatched) && hasLineItems && hasSupplier;
  const shouldShowApproveButton = (isDraft || isMatched) && !isFinalized;

  const matchTotal = computedMatchSummary.total;
  const matchMatched = computedMatchSummary.matched;

  const hasMissingMatches =
    matchTotal > 0 ? matchMatched < matchTotal : !isMatched;

  // Delete button visibility and state
  const isVoided = invoice.status === APInvoiceStatus.VOID;
  const isSyncedToFamous =
    invoice.originSyncStatus === OriginSyncStatus.Success &&
    invoice.originSystem === 'famous';
  const canDelete = (isDraft || isMatched) && !isSyncedToFamous;
  const shouldShowDeleteButton = !isFinalized && !isVoided;

  const deleteTooltip = useMemo(() => {
    if (canDelete) return undefined;
    if (isSyncedToFamous) return 'Cannot delete an invoice synced to Famous';
    if (isFinalized) return 'Cannot delete a finalized invoice';
    if (isVoided) return 'Cannot delete a voided invoice';
    if (!isDraft && !isMatched)
      return 'Only draft or matched invoices can be deleted';
    return undefined;
  }, [canDelete, isSyncedToFamous, isFinalized, isVoided, isDraft, isMatched]);

  function handleDelete() {
    if (!canDelete || deleteInvoice.isPending) return;
    setConfirmDeleteOpen(true);
  }

  function handleConfirmDelete() {
    if (!canDelete || deleteInvoice.isPending) return;
    setConfirmDeleteOpen(false);
    deleteInvoice.mutate({ id: invoice.id });
  }

  const approveTooltip = useMemo(() => {
    if (canApprove) return undefined;
    const missing: string[] = [];
    if (!hasSupplier) missing.push('a supplier');
    if (!hasLineItems) missing.push('at least one line item');
    if (missing.length === 0) return undefined;
    return `Invoice must have ${missing.join(' and ')} to be approved`;
  }, [canApprove, hasSupplier, hasLineItems]);

  const confirmApproveMessage = useMemo(() => {
    const messages: string[] = [];

    if (matchTotal > 0 && matchMatched < matchTotal) {
      messages.push(
        `This invoice has ${matchMatched} of ${matchTotal} line items matched.`,
      );
    } else if (matchTotal === 0 && !isMatched) {
      messages.push('This invoice is not marked as matched.');
    }

    return messages.length > 0 ? messages.join(' ') + ' Approve anyway?' : null;
  }, [matchMatched, matchTotal, isMatched]);

  // Format the scheduled payment date for display in the confirmation dialog
  const scheduledDateDisplay = useMemo(() => {
    if (invoice.scheduledPaymentDate) {
      return formatDateOnlyString(invoice.scheduledPaymentDate);
    }
    return 'Not scheduled';
  }, [invoice.scheduledPaymentDate]);

  function handleApprove() {
    if (!canApprove || approveInvoice.isPending) return;
    // Always show confirmation dialog to confirm scheduled payment date
    setConfirmApproveOpen(true);
  }

  function handleConfirmApprove() {
    if (!canApprove || approveInvoice.isPending) return;
    setConfirmApproveOpen(false);
    approveInvoice.mutate({ id: invoice.id });
  }

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full gap-2 px-3">
      <ResizablePanel
        defaultSize={panelSize}
        onResize={(size) => setPanelSize(size)}
        minSize={25}
        className="flex flex-col pr-2"
      >
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          <InvoiceMetadata
            invoice={invoice}
            derivedAssociations={derivedAssociations}
            lineItemMatchSummary={computedMatchSummary}
          />

          {/* Tabs for Line Items and Charges */}
          {(lineItems && lineItems.length > 0) || headerCharges.length > 0 ? (
            <>
              <Tabs
                defaultValue={
                  lineItems && lineItems.length > 0 ? 'line-items' : 'charges'
                }
                className="w-full"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <TabsList className="w-auto border-b-0">
                    {lineItems && lineItems.length > 0 && (
                      <TabsTrigger value="line-items">
                        Line Items ({lineItems.length})
                      </TabsTrigger>
                    )}
                    {headerCharges.length > 0 && (
                      <TabsTrigger value="charges">
                        Charges ({headerCharges.length})
                      </TabsTrigger>
                    )}
                  </TabsList>
                  {lineItems && lineItems.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setThreeWayMatchOpen(true)}
                      className="gap-1.5 mr-3"
                    >
                      <TableIcon className="h-4 w-4" />
                      3-Way Match
                      {!hasMissingMatches && (
                        <CheckCircleIcon
                          className="h-4 w-4 text-green-600"
                          weight="fill"
                        />
                      )}
                    </Button>
                  )}
                </div>
                {lineItems && lineItems.length > 0 && (
                  <TabsContent value="line-items" className="mt-0">
                    <InvoiceLineItems
                      invoiceId={invoice.id}
                      lineItems={lineItems}
                      currency={invoice.currency}
                      canEditMatches={isDraft || isMatched}
                    />
                  </TabsContent>
                )}
                {headerCharges.length > 0 && (
                  <TabsContent value="charges" className="mt-0">
                    <div className="px-3">
                      <ChargesTable
                        charges={headerCharges}
                        currency={invoice.currency ?? 'USD'}
                      />
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </>
          ) : null}
        </div>

        {/* Action buttons at the bottom */}
        <div className="shrink-0 border-t bg-background pt-3 pb-1 space-y-2">
          {/* ERP Integration Sync Button - conditionally renders based on enabled integrations */}
          <SyncToERPButton invoice={invoice} />

          <Can I={Action.Approve} a={Subject.APInvoice}>
            {shouldShowApproveButton && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      variant="default"
                      size="default"
                      disabled={!canApprove || approveInvoice.isPending}
                      onClick={handleApprove}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      {approveInvoice.isPending
                        ? 'Approving...'
                        : 'Approve Invoice'}
                    </Button>
                  </div>
                </TooltipTrigger>
                {approveTooltip && (
                  <TooltipContent>
                    <p>{approveTooltip}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            )}
          </Can>

          <ConfirmDialog
            open={confirmApproveOpen}
            onClose={() => setConfirmApproveOpen(false)}
            onConfirm={handleConfirmApprove}
            title="Confirm Invoice Approval"
            message={confirmApproveMessage ?? undefined}
            confirmText="Approve Invoice"
            cancelText="Cancel"
            loading={approveInvoice.isPending}
          >
            <div className="space-y-3">
              {/* Scheduled Payment Date Section */}
              <div className="rounded-md border p-3 bg-muted/50">
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                  <CalendarIcon className="h-4 w-4" />
                  Scheduled Payment Date
                </div>
                <div className="text-lg font-semibold">
                  {scheduledDateDisplay}
                </div>
                {invoice.scheduledPaymentExplanation && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1 mb-1">
                      <InfoIcon className="h-3 w-3" />
                      Calculation Details
                    </div>
                    <pre className="whitespace-pre-wrap font-sans">
                      {invoice.scheduledPaymentExplanation}
                    </pre>
                  </div>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Upon approval, a payment will be scheduled for this date.
              </p>
            </div>
          </ConfirmDialog>

          <Can I={Action.Delete} a={Subject.APInvoice}>
            {shouldShowDeleteButton && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      variant="destructive"
                      size="default"
                      disabled={!canDelete || deleteInvoice.isPending}
                      onClick={handleDelete}
                      className="w-full"
                    >
                      {deleteInvoice.isPending
                        ? 'Deleting...'
                        : 'Delete Invoice'}
                    </Button>
                  </div>
                </TooltipTrigger>
                {deleteTooltip && (
                  <TooltipContent>
                    <p>{deleteTooltip}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            )}
          </Can>

          <ConfirmDialog
            open={confirmDeleteOpen}
            onClose={() => setConfirmDeleteOpen(false)}
            onConfirm={handleConfirmDelete}
            title="Delete Invoice?"
            message="This will permanently delete the invoice and all associated data. This action cannot be undone."
            confirmText="Delete Invoice"
            cancelText="Cancel"
            loading={deleteInvoice.isPending}
          />
        </div>
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel minSize={20}>
        {inboundEmail ? (
          <SmartEmailViewer
            emailData={inboundEmailToEmailData(inboundEmail)}
            conversationId={inboundEmail.conversationId}
            defaultTab="attachments"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            No email available
          </div>
        )}
      </ResizablePanel>

      <ThreeWayMatchDialog
        open={threeWayMatchOpen}
        onOpenChange={setThreeWayMatchOpen}
        invoiceId={invoice.id}
        lineItems={lineItems ?? []}
        currency={invoice.currency}
        associatedPOIds={associatedPOIds}
        isMatched={!hasMissingMatches}
      />
    </ResizablePanelGroup>
  );
}
